/**
 * The chrome shared by the four role shells: header, role nav, `<Outlet/>`.
 *
 * Stands in for rent_management_app/lib/widgets/main_shell_layout.dart, which
 * renders one AppBar + a responsive sidebar/bottom-nav and swaps only the nav
 * DESTINATIONS by role (main_shell_layout.dart:44-46, and the per-role index
 * mapping in app_router.dart:119-146). The same factoring is kept here: one
 * frame, four nav tables.
 *
 * Hand-rolled in the app's own rounded/gradient design (`.ui-shell*` in
 * carbon.scss, `--ui-*` tokens) — the same design built for the auth pages,
 * now the app-wide direction rather than Carbon. Confirmed with the user: this
 * is a deliberate reversal of the earlier Carbon adoption, not scope creep —
 * see `.omc/plans/2026-09-04-carbon-design-system-migration.md`'s later
 * entries. `NavItem.icon` stays typed `CarbonIconType`: only Carbon's
 * COMPONENT library is retired here, its icon glyph set is still used
 * (exactly as the auth pages already did).
 *
 * Responsive behaviour is the three variants from `useSideNavVariant`: a
 * drawer below 672px, an icon-only rail 672-1055px, and a persistently
 * expanded labelled nav from 1056px — unchanged from the Carbon version,
 * since that breakpoint logic was never Carbon-specific.
 *
 * `getByRole('heading', { level: 1, name: title })` (router.test.tsx) is a
 * fixed contract of this component: the role title stays an `<h1>` with
 * exactly `title` as its text, separate from the app brand ("Rent
 * Management", linking to this shell's own dashboard).
 */
import { useState } from 'react';
import { Link, Outlet, useMatch } from 'react-router-dom';
import type { CarbonIconType } from '@carbon/icons-react';
import { Building, Close, Logout, Menu } from '@carbon/icons-react';
import { useAuth } from '../auth/AuthContext';
import { fullName } from '../types/user';
import { NotificationBell } from '../components/NotificationBell';
import { ThemeToggle } from '../components/ThemeToggle';
import { useSideNavVariant, type SideNavVariant } from '../lib/useSideNavVariant';

export interface NavItem {
  label: string;
  /** Absolute path. */
  to: string;
  /** Pass for the shell's index route so it does not stay highlighted everywhere. */
  end?: boolean;
  /** Rendered before the label; Carbon's icon glyphs, not its component library. */
  icon?: CarbonIconType;
}

export interface ShellFrameProps {
  title: string;
  navItems: readonly NavItem[];
}

/**
 * One nav entry. A component of its own — not inlined into the `.map()` in
 * `ShellFrame` — because it calls `useMatch`, and a hook belongs in a
 * component that renders once per item, never inside the callback passed to
 * `.map()`.
 *
 * `aria-label` is always set to `item.label`, and the visible text span is
 * only rendered when `showLabel` is true (drawer/expanded, not rail) — this
 * keeps the link's accessible name identical across all three variants
 * without a visually-hidden-text CSS trick that `display:none` would break
 * for screen readers.
 */
function ShellNavLink({
  item,
  showLabel,
  onNavigate,
}: {
  item: NavItem;
  showLabel: boolean;
  onNavigate: () => void;
}): React.JSX.Element {
  const isActive = useMatch({ path: item.to, end: item.end ?? false }) !== null;
  const Icon = item.icon;

  return (
    <li>
      <Link
        to={item.to}
        className={isActive ? 'ui-shell__nav-link ui-shell__nav-link--active' : 'ui-shell__nav-link'}
        aria-label={item.label}
        onClick={onNavigate}
        {...(isActive && { 'aria-current': 'page' })}
      >
        {Icon !== undefined && (
          <span className="ui-shell__nav-icon" aria-hidden="true">
            <Icon size={18} />
          </span>
        )}
        {showLabel && <span className="ui-shell__nav-label">{item.label}</span>}
      </Link>
    </li>
  );
}

function menuIconFor(variant: SideNavVariant, open: boolean): React.JSX.Element {
  if (variant === 'drawer' && open) return <Close size={20} />;
  return <Menu size={20} />;
}

export function ShellFrame({ title, navItems }: ShellFrameProps): React.JSX.Element {
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const variant = useSideNavVariant();
  const isDrawer = variant === 'drawer';
  const drawerExpanded = isDrawer && drawerOpen;
  // The dashboard entry is always listed first (`end: true`) — see every
  // *Shell.tsx nav table — so it doubles as "home" for the brand link.
  const homePath = navItems[0]?.to ?? '/';

  function closeDrawer(): void {
    if (isDrawer) setDrawerOpen(false);
  }

  return (
    <div className="ui-shell">
      <a href="#main-content" className="ui-shell__skip-link">
        Skip to main content
      </a>

      <header className="ui-shell__header">
        <div className="ui-shell__header-start">
          {isDrawer && (
            <button
              type="button"
              className="ui-shell__menu-toggle"
              aria-label={drawerExpanded ? 'Close navigation' : 'Open navigation'}
              aria-expanded={drawerExpanded}
              onClick={() => setDrawerOpen((open) => !open)}
            >
              {menuIconFor(variant, drawerExpanded)}
            </button>
          )}
          <Link to={homePath} className="ui-shell__brand">
            <span className="ui-shell__brand-logo" aria-hidden="true">
              <Building size={16} />
            </span>
            <span className="ui-shell__brand-name">Rent Management</span>
          </Link>
        </div>

        <div className="ui-shell__heading">
          <h1 className="ui-shell__title">{title}</h1>
          {user !== null && (
            <p className="ui-shell__welcome">
              Welcome back, {user.first_name !== '' ? user.first_name : fullName(user)}
            </p>
          )}
        </div>

        <div className="ui-shell__header-end">
          <ThemeToggle />
          <NotificationBell />
          <button type="button" className="ui-shell__icon-button" aria-label="Log out" onClick={() => void logout()}>
            <Logout size={18} />
          </button>
        </div>
      </header>

      {isDrawer && (
        <div
          className="ui-shell__backdrop"
          hidden={!drawerExpanded}
          onClick={closeDrawer}
          aria-hidden="true"
        />
      )}

      <nav
        className={
          drawerExpanded ? `ui-shell__nav ui-shell__nav--${variant} ui-shell__nav--open` : `ui-shell__nav ui-shell__nav--${variant}`
        }
        aria-label={`${title} navigation`}
      >
        <ul className="ui-shell__nav-list">
          {navItems.map((item) => (
            <ShellNavLink key={item.to} item={item} showLabel={variant !== 'rail'} onNavigate={closeDrawer} />
          ))}
        </ul>
      </nav>

      {/* `shell__content` kept alongside the new class: legacy.css's descendant
          selectors (`.shell__content table`, `.shell__content > section`, …)
          still style every not-yet-converted page rendered through here. */}
      <main id="main-content" className="shell__content ui-shell__content">
        <Outlet />
      </main>
    </div>
  );
}

export default ShellFrame;
